import { ChevronsUpDown, X } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Controller, type FieldValues } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { FieldShell } from "./FieldShell";
import {
  toOptions,
  useFieldControl,
  type BaseFieldProps,
  type SelectOption
} from "./fieldTypes";

export type ComboboxFieldProps<T extends FieldValues> = BaseFieldProps<T> & {
  options: ReadonlyArray<string | SelectOption>;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Shows a clear action once something is chosen. Defaults to `!required`. */
  clearable?: boolean;
};

/**
 * Searchable single select (Popover + Command) bound to a string field.
 * A stored value that isn't in `options` is still shown and selectable, so
 * legacy data never silently blanks.
 */
export function ComboboxField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  description,
  disabled,
  className,
  id: idProp,
  options,
  placeholder = "Select",
  searchPlaceholder = "Search…",
  emptyText = "No matches.",
  clearable
}: ComboboxFieldProps<T>) {
  const ctrl = useFieldControl(control);
  const autoId = useId();
  const id = idProp ?? autoId;
  const listId = `${id}-listbox`;
  const [open, setOpen] = useState(false);
  const canClear = clearable ?? !required;
  const flat = useMemo(() => toOptions(options), [options]);

  return (
    <Controller
      control={ctrl}
      name={name}
      render={({ field, fieldState }) => {
        const value = String(field.value ?? "");
        const known = flat.find((o) => o.value === value);
        const items =
          value && !known ? [{ value, label: value }, ...flat] : flat;
        const selectedLabel = known?.label ?? (value || null);
        return (
          <FieldShell
            id={id}
            label={label}
            required={required}
            description={description}
            error={fieldState.error}
            disabled={disabled}
            className={className}
          >
            <div className="flex w-full items-center gap-1">
              {/* `modal` keeps the list scrollable inside Sheet / Dialog drawers,
                  whose scroll lock otherwise blocks wheel over the popover. */}
              <Popover open={open} onOpenChange={setOpen} modal>
                <PopoverTrigger asChild>
                  <Button
                    id={id}
                    ref={field.ref}
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listId}
                    aria-invalid={fieldState.invalid || undefined}
                    disabled={disabled}
                    onBlur={field.onBlur}
                    className={cn(
                      "h-8 min-w-0 flex-1 justify-between px-2.5 font-normal",
                      !selectedLabel && "text-muted-foreground",
                      fieldState.invalid &&
                        "border-destructive ring-3 ring-destructive/20"
                    )}
                  >
                    <span className="truncate">
                      {selectedLabel ?? placeholder}
                    </span>
                    <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-(--radix-popover-trigger-width) min-w-56 p-0"
                >
                  <Command>
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList id={listId}>
                      <CommandEmpty>{emptyText}</CommandEmpty>
                      <CommandGroup>
                        {items.map((o) => (
                          <CommandItem
                            key={o.value}
                            value={o.label}
                            data-checked={o.value === value || undefined}
                            onSelect={() => {
                              field.onChange(o.value);
                              setOpen(false);
                            }}
                          >
                            {o.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {canClear && value && !disabled ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Clear ${
                    typeof label === "string" ? label : ""
                  }`.trim()}
                  onClick={() => field.onChange("")}
                >
                  <X className="size-3.5" />
                </Button>
              ) : null}
            </div>
          </FieldShell>
        );
      }}
    />
  );
}
